import React from "react";
import { useApolloClient } from "@apollo/client/react";
import { useWorkspace, type Workspace } from "#app/lib/data/workspace-context";
import { useDebouncedValue } from "#app/lib/hooks/use-debounced-value";
import { CreateWorkspaceDocument, ValidateWorkspaceUrlPathDocument } from "#app/lib/data";
import { toUrlFriendly, toUrlFriendlyInput } from "@salestrak/utilities";
import type { Color } from "@salestrak/ui";

export { toUrlFriendly };

/**
 * Typing-friendly identifier normalization (`toUrlFriendlyInput`): accents
 * transliterated, runs of hyphens collapsed, one trailing hyphen kept so the
 * user can type "my-co". `toUrlFriendly` is still applied on validate/submit.
 */
export function normalizeIdentifierInput(value: string): string {
  return toUrlFriendlyInput(value);
}

const URL_PATH_MIN_LENGTH = 3;
const URL_PATH_MAX_LENGTH = 32;

/** Length bounds on the submitted slug; null when in range or empty
 *  (required-ness is the form's call, not the slug contract's). */
function urlPathLengthError(urlPath: string): string | null {
  if (urlPath.length > URL_PATH_MAX_LENGTH) {
    return `Too long, cannot be more than ${URL_PATH_MAX_LENGTH} characters`;
  }
  if (urlPath.length > 0 && urlPath.length < URL_PATH_MIN_LENGTH) {
    return `Too short, must be at least ${URL_PATH_MIN_LENGTH} characters`;
  }
  return null;
}

/**
 * Workspace-create data layer. Watches `identifier` and live-validates the
 * derived slug (debounced) against `validateWorkspaceUrlPath`; exposes the
 * create mutation and workspace entry (whose `switchWorkspace` clears the
 * Apollo store) for the create screen.
 */
export function useWorkspaceCreate(identifier: string) {
  const client = useApolloClient();
  const { switchWorkspace } = useWorkspace();

  const [uniqueError, setUniqueError] = React.useState<string | null>(null);
  const [isCreating, setIsCreating] = React.useState(false);

  const urlPath = toUrlFriendly(identifier);
  const debouncedUrlPath = useDebouncedValue(urlPath, 300);

  // Length is checked synchronously (live as the user types); only an
  // in-bounds slug is worth a round trip to the uniqueness check.
  const lengthError = urlPathLengthError(urlPath);

  React.useEffect(() => {
    if (!debouncedUrlPath || urlPathLengthError(debouncedUrlPath)) {
      setUniqueError(null);
      return;
    }
    let stale = false;
    client
      .query({
        query: ValidateWorkspaceUrlPathDocument,
        variables: { urlPath: debouncedUrlPath },
        fetchPolicy: "network-only",
      })
      .then(({ data }) => {
        if (stale || !data) return;
        setUniqueError(data.validateWorkspaceUrlPath ? null : "Must be unique");
      })
      .catch(() => {
        // ignore validation errors — the create mutation is the backstop
      });
    return () => {
      stale = true;
    };
  }, [client, debouncedUrlPath]);

  const urlPathError = lengthError ?? uniqueError;

  const createWorkspace = React.useCallback(
    async (name: string, identifierValue: string, avatarColor: Color) => {
      setIsCreating(true);
      try {
        const { data, error } = await client.mutate({
          mutation: CreateWorkspaceDocument,
          variables: {
            input: {
              name: name.trim(),
              urlPath: toUrlFriendly(identifierValue),
              avatarColor,
            },
          },
        });
        // errorPolicy "all" resolves with `error` set instead of rejecting —
        // rethrow so the caller's catch path stays the failure path.
        if (!data) throw error ?? new Error("Failed to create workspace");
        return data.createWorkspace as Workspace;
      } finally {
        setIsCreating(false);
      }
    },
    [client],
  );

  const enterWorkspace = React.useCallback(
    async (workspace: Workspace) => {
      await switchWorkspace(workspace);
    },
    [switchWorkspace],
  );

  return {
    /** The slug that will actually be submitted, for URL previews. */
    urlPath,
    urlPathError,
    isCreating,
    createWorkspace,
    enterWorkspace,
  };
}
